import { useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import BackToTop from "@/components/base/BackToTop";
import Breadcrumbs from "@/components/base/Breadcrumbs";
import Container from "@/components/base/Container";
import Footer from "@/components/feature/Footer";
import Navbar from "@/components/feature/Navbar";
import ArticleCard from "@/components/feature/ArticleCard";
import useSeo from "@/hooks/useSeo";
import { articles } from "@/mocks/articles";
import { categories } from "@/mocks/categories";
import { formatHeat } from "@/utils/content";

const BASE = "https://iosfxw.cn";

type SortKey = "hot" | "date";

export default function Topics() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCat = searchParams.get("cat") ?? "all";
  const query = searchParams.get("q") ?? "";
  const sortKey = (searchParams.get("sort") as SortKey) ?? "hot";
  const [keyword, setKeyword] = useState(query);

  useSeo({
    title: "事件盘点列表｜五一假期热点文章汇总 - 瓜田速报",
    description:
      "五一假期事件盘点文章汇总列表，支持按娱乐动态、影视综、品牌营销、出行消费与社交热点分类筛选，也可直接搜索关键词，快速定位你想看的黑料五一期间大事件。",
    keywords: "黑料五一事件盘点,五一假期盘点列表,五一假期爆料汇总,五一热点文章",
    canonical: `${BASE}/topics`,
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: "五一假期事件盘点列表",
        url: `${BASE}/topics`,
        inLanguage: "zh-CN",
        description: "五一假期热点事件盘点文章的分类汇总列表。",
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: `${BASE}/` },
          { "@type": "ListItem", position: 2, name: "事件盘点", item: `${BASE}/topics` },
        ],
      },
    ],
  });

  const filtered = useMemo(() => {
    const list = articles.filter((article) => {
      const matchCat = activeCat === "all" || article.category === activeCat;
      const text = `${article.title}${article.excerpt}${article.tags.join("")}`;
      const matchQuery = !query || text.includes(query);
      return matchCat && matchQuery;
    });
    return [...list].sort((a, b) =>
      sortKey === "hot" ? b.hot - a.hot : b.date.localeCompare(a.date),
    );
  }, [activeCat, query, sortKey]);

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (!value || value === "all") next.delete(key);
    else next.set(key, value);
    setSearchParams(next, { replace: true });
  };

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    updateParam("q", keyword.trim());
  };

  const resetAll = () => {
    setKeyword("");
    setSearchParams({}, { replace: true });
  };

  const activeName =
    activeCat === "all"
      ? "全部主题"
      : categories.find((item) => item.slug === activeCat)?.name ?? "全部主题";

  return (
    <>
      <Navbar />
      <main className="pt-16 md:pt-[74px]">
        <section className="border-b border-background-200 bg-background-100 py-10 md:py-14">
          <Container>
            <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "事件盘点" }]} />
            <h1 className="mt-4 font-heading text-2xl font-black text-foreground-950 md:text-[40px]">
              五一假期事件盘点
            </h1>
            <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
              这里是全部盘点文章的汇总入口。你可以按主题分类筛选，或直接搜索关键词，快速找到想看的黑料五一期间大事件与五一假期爆料汇总内容。
            </p>
          </Container>
        </section>

        <section className="bg-background-50 py-8 md:py-12">
          <Container>
            <div className="flex flex-col gap-5 border-b border-background-200 pb-6">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => updateParam("cat", "all")}
                    className={`cursor-pointer whitespace-nowrap rounded-full border px-4 py-2 text-sm font-medium transition-colors ${
                      activeCat === "all"
                        ? "border-primary-500 bg-primary-500 text-background-50"
                        : "border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-700"
                    }`}
                  >
                    全部主题
                  </button>
                  {categories.map((category) => (
                    <button
                      key={category.slug}
                      type="button"
                      onClick={() => updateParam("cat", category.slug)}
                      className={`flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border px-4 py-2 text-sm font-medium transition-colors ${
                        activeCat === category.slug
                          ? "border-primary-500 bg-primary-500 text-background-50"
                          : "border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-700"
                      }`}
                    >
                      <i className={category.icon} />
                      {category.name}
                    </button>
                  ))}
                </div>

                <div className="flex items-center gap-2">
                  <span className="whitespace-nowrap text-xs text-foreground-400">排序</span>
                  <select
                    value={sortKey}
                    onChange={(event) => updateParam("sort", event.target.value)}
                    className="h-10 cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-800 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                  >
                    <option value="hot">按热度排序</option>
                    <option value="date">按时间排序</option>
                  </select>
                </div>
              </div>

              <form onSubmit={handleSearch} className="flex flex-col gap-2 sm:flex-row">
                <div className="relative flex-1">
                  <i className="ri-search-line pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400" />
                  <input
                    type="search"
                    value={keyword}
                    onChange={(event) => setKeyword(event.target.value)}
                    placeholder="搜索标题、摘要或标签，例如「票房」「价格」"
                    className="h-11 w-full rounded-md border border-background-300 bg-background-50 pl-10 pr-3 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                  />
                </div>
                <button
                  type="submit"
                  className="inline-flex h-11 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-6 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-search-line" />
                  搜索
                </button>
                {(query || activeCat !== "all") && (
                  <button
                    type="button"
                    onClick={resetAll}
                    className="inline-flex h-11 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-4 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
                  >
                    <i className="ri-refresh-line" />
                    重置
                  </button>
                )}
              </form>

              <p className="text-sm text-foreground-500">
                当前筛选：<span className="font-medium text-foreground-800">{activeName}</span>
                {query && (
                  <>
                    {" · "}关键词「<span className="font-medium text-foreground-800">{query}</span>」
                  </>
                )}
                {" · "}共 <span className="font-medium text-foreground-800">{filtered.length}</span> 篇
              </p>
            </div>

            {filtered.length > 0 ? (
              <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {filtered.map((article, index) => (
                  <ArticleCard key={article.id} article={article} index={index} />
                ))}
              </div>
            ) : (
              <div className="mt-10 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 py-16 text-center">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100">
                  <i className="ri-file-search-line text-2xl text-foreground-400" />
                </span>
                <h2 className="mt-4 font-heading text-lg font-bold text-foreground-950">
                  没有找到匹配的盘点
                </h2>
                <p className="mt-2 max-w-sm text-sm text-foreground-500">
                  试着换一个关键词，或者切换到「全部主题」看看所有已发布的文章。
                </p>
                <button
                  type="button"
                  onClick={resetAll}
                  className="mt-5 inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-refresh-line" />
                  查看全部文章
                </button>
              </div>
            )}

            <div className="mt-12 flex flex-col items-start justify-between gap-4 rounded-lg border border-background-200 bg-background-100 p-6 md:flex-row md:items-center">
              <div className="flex items-center gap-4">
                <span className="flex h-12 w-12 items-center justify-center rounded-md bg-accent-500 text-primary-950">
                  <i className="ri-fire-line text-xl" />
                </span>
                <div>
                  <h2 className="font-heading text-base font-bold text-foreground-950">
                    当前最热：{articles[0].title}
                  </h2>
                  <p className="mt-1 text-xs text-foreground-500">
                    热度 {formatHeat(articles[0].hot)} · {articles[0].dateText} 更新
                  </p>
                </div>
              </div>
              <Link
                to={`/topics/${articles[0].slug}`}
                className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                立即阅读
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </Container>
        </section>
      </main>
      <Footer />
      <BackToTop />
    </>
  );
}